import { ApiHandler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { GetCartApiParameter, getCart } from "@insite/client-framework/Services/CartService";
import { Session, watchForOtherTabSessionChange } from "@insite/client-framework/Services/SessionService";
import {
    DispatchCompleteSignIn,
    NavigateToReturnUrl,
    RedirectToChangeCustomer,
    SignInParameter,
    SignInResult,
    chain,
} from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

interface SignInParameterCustom extends SignInParameter {
    onSuccess?: (result: any) => void;
    onComplete?: (result: any) => void;
}
type HandlerType = ApiHandler<
    SignInParameterCustom,
    SignInResult,
    {
        tokenSuccessfullyRequested?: boolean;
        authenticatedSession?: Session;
    }
>;

export const DispatchCompleteSignInCustom: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteSignIn",
        accessToken: props.apiResult?.access_token,
    });
    sessionStorage.setItem("selectedInvoices", JSON.stringify([]));
    props.parameter && props.parameter.onComplete && props.parameter?.onComplete(props.apiResult);
};

export const NavigateToReturnUrlCustom: HandlerType = async props => {
    if (!props.authenticatedSession) {
        return;
    }

    const state = props.getState();
    let returnUrl = props.parameter.returnUrl;
    const dashboardPageUrl = getPageLinkByPageType(props.getState(), "MyAccountPage")?.url;
    const defaultUrl =
        props.authenticatedSession.dashboardIsHomepage && dashboardPageUrl
            ? dashboardPageUrl
            : props.authenticatedSession.customLandingPage
                ? props.authenticatedSession.customLandingPage
                : getHomePageUrl(state);
    const checkoutShippingUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;

    if (returnUrl?.toLowerCase() === checkoutShippingUrl?.toLowerCase()) {
        const cartResult = await getCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: ["cartLines", "hiddenproducts"],
        } as GetCartApiParameter);

        const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;
        const checkoutReviewAndSubmitUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
        const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;

        if (!canCheckOut || props.authenticatedSession.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    }

    if (returnUrl?.includes("SwitchingLanguage")) {
        returnUrl = returnUrl.split("?")[0];
    }

    if (
        returnUrl?.toLowerCase() === getHomePageUrl(state).toLowerCase() &&
        props.authenticatedSession?.customLandingPage
    ) {
        // need to send to custom landing page if the return URL is a language specific homepage
        if (returnUrl?.endsWith("/")) {
            returnUrl = returnUrl.substring(0, returnUrl.length - 1);
        }
        if (props.authenticatedSession?.customLandingPage.startsWith("/")) {
            returnUrl = `${returnUrl}${props.authenticatedSession?.customLandingPage}`;
        } else {
            returnUrl = `${returnUrl}/${props.authenticatedSession?.customLandingPage}`;
        }
    } else if (
        returnUrl?.toLowerCase() === getHomePageUrl(state).toLowerCase() &&
        props.authenticatedSession.dashboardIsHomepage &&
        dashboardPageUrl
    ) {
        // case where returnUrl is a language-specific homepage but we should redirect to the dashboard
        returnUrl = defaultUrl;
    }
    if (returnUrl?.includes("signin")) {
        returnUrl = "/";
    }
    window.location.href = returnUrl || defaultUrl;
};

replaceInChain(chain, DispatchCompleteSignIn, DispatchCompleteSignInCustom);
replaceInChain(chain, NavigateToReturnUrl, NavigateToReturnUrlCustom);
